import React from 'react';
import { createRoot } from 'react-dom/client';
import 'react-virtualized/styles.css';
import './styles.css';
import { PropertyCalendarGrid } from './components/PropertyCalendarGrid.tsx';

console.log('📦 Bundle with react-virtualized loaded!');

// Initialize local development
function initializeLocalDevelopment() {
  const rootElement = document.getElementById('root');
  if (!rootElement) return;

  // Start with loading state (no data provided)
  if (window.MyBubblePlugin) {
    window.MyBubblePlugin.init({
      container: rootElement,
      startDate: new Date('2025-01-23'),
      endDate: new Date('2025-03-01')
      // No properties, reservations, tasks provided = loading state
    });
  }

  // Simulate loading delay, then load data
  setTimeout(() => {
    fetch('/sample-data.json')
      .then(response => response.json())
      .then(sampleData => {
        // console.log('📦 Sample data loaded for local dev:', sampleData);
        
        if (window.MyBubblePlugin) {
          window.MyBubblePlugin.init({
            container: rootElement,
            properties: sampleData.properties,
            reservations: sampleData.reservations,
            tasks: sampleData.tasks,
            departments: sampleData.departments,
            startDate: new Date('2025-01-23'),
            endDate: new Date('2025-03-01'),
            loading: false
          });
        }
      })
      .catch(error => {
        console.error('❌ Failed to load sample data:', error);
        
        // Fallback: render without data
        if (window.MyBubblePlugin) {
          window.MyBubblePlugin.init({
            container: rootElement,
            properties: [],
            reservations: [],
            tasks: [],
            departments: [],
            startDate: new Date('2025-01-23'),
            endDate: new Date('2025-03-01'),
            loading: false
          });
        }
      });
  }, 1000); // 3 second loading simulation
}

// Load sample data for local development
if (document.getElementById('root')) {
  initializeLocalDevelopment();
}

// Define plugin interface
interface PluginOptions {
  container?: HTMLElement;
  reservations?: any[];
  tasks?: any[];
  properties?: any[];
  departments?: any[];
  startDate?: Date | string;
  endDate?: Date | string;
  bubbleInstance?: any;
  loading?: boolean;
}

// Create plugin object for Bubble integration
const createBubblePlugin = () => ({
  init: (options: PluginOptions) => {
    console.log('🚀 MyBubblePlugin.init called with property calendar');
    // console.log('Options received:', options);
    
    if (!options.container) {
      console.warn('No container provided to plugin');
      return 'Error: No container provided';
    }

    // Convert string dates to Date objects if needed
    const startDate = options.startDate 
      ? (typeof options.startDate === 'string' ? new Date(options.startDate) : options.startDate) 
      : undefined;
    const endDate = options.endDate 
      ? (typeof options.endDate === 'string' ? new Date(options.endDate) : options.endDate) 
      : undefined;
    
    // Log data for debugging
    // console.log('🔍 Properties data:', options.properties);
    // console.log('🔍 Reservations data:', options.reservations);
    // console.log('🔍 Tasks data:', options.tasks);
    // console.log('🔍 Departments data:', options.departments);
    // console.log('🔍 Date range:', startDate, 'to', endDate);
    
    // Determine if data is loaded (arrays provided, even if empty)
    const dataProvided = options.properties !== undefined && 
                        options.reservations !== undefined && 
                        options.tasks !== undefined;
    
    // Render the calendar grid
    const root = createRoot(options.container);
    root.render(
      <PropertyCalendarGrid 
        properties={options.properties} 
        reservations={options.reservations} 
        tasks={options.tasks}
        departments={options.departments}
        startDate={startDate}
        endDate={endDate}
        bubbleInstance={options.bubbleInstance}
        loading={options.loading}
      />
    );
    
    // console.log('📊 Property calendar grid rendered in container');
    return 'Property calendar plugin initialized';
  }
});

// Attach plugin to window
(window as any).MyBubblePlugin = createBubblePlugin();

// Global type declarations
declare global {
  interface Window {
    MyBubblePlugin?: any;
  }
}

// console.log('🎉 Bundle loaded! React-virtualized Grid available via MyBubblePlugin.');

